import { Alert, Button, Stack } from '@mui/material';
import { useAuth } from '../../auth/AuthProvider';
import { useInOrganisation } from '../../hooks/useInOrganisation';
import { useEnterTenant } from '../../hooks/useEnterTenant';
import { useSubTenants } from '../../hooks/api';

/**
 * Why an organisation's operational screen is empty.
 *
 * A PARENT holds no vehicles, incidents or telemetry by construction (design
 * D2) — every one of them belongs to a branch. So the fleet page at an
 * organisation is legitimately empty, and said nothing about it: the table read
 * *"No results — try adjusting your filters or search"* with no filter set, and
 * the map read *"No active vehicles"* beside a console that had just reported
 * four.
 *
 * Both are absences presented as facts, and the second one blames the reader for
 * a search they did not make. Reported three times in one afternoon by somebody
 * who had been told the rule twice, which is the signal that an explanation is
 * not the fix.
 *
 * Shown only when there is genuinely nothing AND no filter is applied — a parent
 * with a stray vehicle on it should show that vehicle, and an empty result that
 * a search DID cause is the ordinary message, correctly.
 */
export function ParentHoldsNothingNotice({
  what,
  filtered,
  empty,
}: {
  /** What this screen lists, in the plural: "vehicles", "incidents". */
  what: string;
  /** A search or filter is in force, so emptiness has an ordinary explanation. */
  filtered: boolean;
  empty: boolean;
}) {
  const { tenantId, tenantChoices, assumedTenant } = useAuth();
  const inOrganisation = useInOrganisation();
  const enterTenant = useEnterTenant();

  /*
   * ⚠️ The name comes from the ASSUMED tenant first, and the branches from the
   * sub-tenant LIST rather than from `tenantChoices`.
   *
   * Both used to read `tenantChoices`, which never contains an assumed tenant —
   * so `here` was undefined for a platform administrator and the component
   * returned null. The notice has been silently inert for them on every page it
   * was applied to, which is a large part of why "an organisation's screens are
   * empty and say the wrong thing" kept being reported after it was supposedly
   * fixed: the explanation existed and never reached the reader most likely to
   * be looking at it.
   *
   * ⚠️ And `tenantChoices` was the wrong source for BRANCHES even for a member:
   * it lists branches they hold a membership in, not the organisation's
   * branches. A tenant administrator with one membership saw one button beside
   * a company with four.
   */
  const branchQuery = useSubTenants(inOrganisation);
  const hereName =
    assumedTenant?.id === tenantId
      ? assumedTenant.name
      : tenantChoices.find((c) => c.tenantId === tenantId)?.tenantName;
  const branches = (branchQuery.data?.items ?? []).filter((b) => b.status === 'ACTIVE');

  if (!empty || filtered || !inOrganisation || !hereName) return null;

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Stack spacing={1}>
        <span>
          {hereName} is the organisation, and an organisation holds no {what} itself —
          they belong to its branches.
          {branches.length > 0
            ? ' Open one to see them.'
            : ' It has no branches yet, so there are none to see.'}
        </span>
        {branches.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {/*
              The branches by NAME, as buttons. A sentence saying "use the
              switcher" describes the fix; this performs it — and the switcher
              is the control people had not found, which is how this went
              unnoticed three times.
            */}
            {branches.map((b) => (
              <Button
                key={b.id}
                size="small"
                variant="outlined"
                // Switch or assume, whichever the reader has — see
                // `useEnterTenant`. `setTenant` alone did nothing at all for a
                // platform administrator, who holds no membership anywhere.
                onClick={() => enterTenant(b.id, b.name, false)}
              >
                {b.name}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
